import { useMutation, useQuery, useQueryClient, useInfiniteQuery } from '@tanstack/react-query'
import { pressReleaseService } from '../services/press-release'
import type { PressReleaseFilterParams, PaginationParams, CreatePressRelease, UpdatePressRelease } from '../types'

// Query keys for press release management
export const pressReleaseKeys = {
  all: ['press-release'] as const,
  pressReleases: () => [...pressReleaseKeys.all, 'press-releases'] as const,
  pressRelease: (id: string) => [...pressReleaseKeys.pressReleases(), id] as const,
  pressReleasesPaginated: (params: PaginationParams, filters: PressReleaseFilterParams) =>
    [...pressReleaseKeys.pressReleases(), 'paginated', params, filters] as const,
  pressReleasesAll: () => [...pressReleaseKeys.pressReleases(), 'all'] as const,
  pressReleasesPublic: () => [...pressReleaseKeys.pressReleases(), 'public'] as const,
  pressReleasesSearch: (query: string) => [...pressReleaseKeys.pressReleases(), 'search', query] as const,
  stats: () => [...pressReleaseKeys.all, 'stats'] as const,
  tags: () => [...pressReleaseKeys.all, 'tags'] as const
}


export const usePressReleases = (
  params: PaginationParams = { page: 1, size: 10 },
  filters: PressReleaseFilterParams = {}
) => {
  return useQuery({
    queryKey: pressReleaseKeys.pressReleasesPaginated(params, filters),
    queryFn: () => pressReleaseService.getPressReleases(params, filters),
    staleTime: 2 * 60 * 1000, // 2 minutes
    placeholderData: (previousData) => previousData
  })
}


export const useAllPressReleases = () => {
  return useQuery({
    queryKey: pressReleaseKeys.pressReleasesAll(),
    queryFn: () => pressReleaseService.getAllPressReleases(),
    staleTime: 5 * 60 * 1000, 
    enabled: false
  })
}


export const usePublicPressReleases = () => {
  return useQuery({
    queryKey: pressReleaseKeys.pressReleasesPublic(),
    queryFn: () => pressReleaseService.getPublicPressReleases(),
    staleTime: 10 * 60 * 1000, // 10 minutes
    enabled: false
  })
}


export const usePressRelease = (pressReleaseId: string) => {
  return useQuery({
    queryKey: pressReleaseKeys.pressRelease(pressReleaseId),
    queryFn: () => pressReleaseService.getPressReleaseById(pressReleaseId),
    enabled: !!pressReleaseId,
    staleTime: 5 * 60 * 1000 // 5 minutes
  })
}


export const useSearchPressReleases = (searchQuery: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: pressReleaseKeys.pressReleasesSearch(searchQuery),
    queryFn: () => pressReleaseService.searchPressReleases(searchQuery),
    enabled: enabled && !!searchQuery && searchQuery.length > 2 && false, // Disabled until endpoint is implemented
    staleTime: 2 * 60 * 1000 // 2 minutes
  })
}


export const useInfinitePressReleases = (baseFilters: PressReleaseFilterParams = {}) => {
  return useInfiniteQuery({
    queryKey: [...pressReleaseKeys.pressReleases(), 'infinite', baseFilters],
    queryFn: ({ pageParam }: { pageParam: number }) =>
      pressReleaseService.getPressReleases({ page: pageParam, size: 10 }, baseFilters),
    initialPageParam: 1,
    getNextPageParam: (lastPage: any, allPages: any[]) => {
      if (!lastPage.success || !lastPage.data) return undefined

      const currentPage = allPages.length
      const totalPages = lastPage.data.totalPage

      return currentPage < totalPages ? currentPage + 1 : undefined
    },
    staleTime: 2 * 60 * 1000
  })
}


export const usePressReleaseStats = () => {
  return useQuery({
    queryKey: pressReleaseKeys.stats(),
    queryFn: async () => {
      const [draftResponse, publishedResponse] = await Promise.all([
        pressReleaseService.getTotalDraftPressReleases(),
        pressReleaseService.getTotalPublishedPressReleases()
      ])

      return {
        draft: draftResponse.data || 0,
        published: publishedResponse.data || 0,
        total: 0 // TODO: Implement when endpoint is documented
      }
    },
    staleTime: 5 * 60 * 1000 
  })
}


export const useArticleTags = () => {
  return useQuery({
    queryKey: pressReleaseKeys.tags(),
    queryFn: () => pressReleaseService.getArticleTags(),
    staleTime: 30 * 60 * 1000, 
    enabled: false 
  })
}


export const useCreatePressRelease = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreatePressRelease) => pressReleaseService.createPressRelease(data),
    onSuccess: () => {
      
      queryClient.invalidateQueries({ queryKey: pressReleaseKeys.pressReleases() })
      queryClient.invalidateQueries({ queryKey: pressReleaseKeys.stats() })
    },
    onError: (error) => {
      console.error('Press release creation failed:', error)
    }
  })
}


export const useUpdatePressRelease = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ pressReleaseId, data }: { pressReleaseId: string; data: UpdatePressRelease }) =>
      pressReleaseService.updatePressRelease(pressReleaseId, data),
    onSuccess: (_response, { pressReleaseId }) => {

      queryClient.invalidateQueries({ queryKey: pressReleaseKeys.pressRelease(pressReleaseId) })


      queryClient.invalidateQueries({ queryKey: pressReleaseKeys.pressReleases() })
    },
    onError: (error) => {
      console.error('Press release update failed:', error)
    }
  })
}


export const useUpdatePressReleaseStatus = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ pressReleaseId, status }: { pressReleaseId: string; status: 'DRAFT' | 'PUBLISH' }) =>
      pressReleaseService.updatePressReleaseStatus(pressReleaseId, status),
    onSuccess: (response, { pressReleaseId }) => {
     
      if (response.success && response.data) {
        queryClient.setQueryData(pressReleaseKeys.pressRelease(pressReleaseId), response.data)
      }

     
      queryClient.invalidateQueries({ queryKey: pressReleaseKeys.pressReleases() })
      queryClient.invalidateQueries({ queryKey: pressReleaseKeys.stats() })
    },
    onError: (error) => {
      console.error('Press release status update failed:', error)
    }
  })
}


export const useDeletePressRelease = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (pressReleaseId: string) => pressReleaseService.deletePressRelease(pressReleaseId),
    onSuccess: (_, pressReleaseId) => {
   
      queryClient.removeQueries({ queryKey: pressReleaseKeys.pressRelease(pressReleaseId) })

      
      queryClient.invalidateQueries({ queryKey: pressReleaseKeys.pressReleases() })
      queryClient.invalidateQueries({ queryKey: pressReleaseKeys.stats() })
    },
    onError: (error) => {
      console.error('Press release deletion failed:', error)
    }
  })
}
